import { Injectable, inject } from '@angular/core';
import { DocNode, DocumentType, VirusScanStatus } from '@qtek/shared/models';
import { map, Observable, of, race, take, filter, timer } from 'rxjs';
import { DocumentsService } from './documents.service';

/** How a freshly uploaded doc's async virus scan settled. */
export type ScanOutcome = 'ready' | 'failed' | 'timeout';

const DEFAULT_TIMEOUT_MS = 120_000;

/** Servable = no scan pipeline (null) or scan passed. */
export function isServable(vsts: VirusScanStatus | null | undefined): boolean {
  return vsts == null || vsts === VirusScanStatus.OK;
}

function isScanFailure(vsts: VirusScanStatus | null | undefined): boolean {
  return (
    vsts === VirusScanStatus.CONTAMINATED || vsts === VirusScanStatus.FAILED
  );
}

/** Extract the doc matching `docId` from a `docref` WS message, if present. */
function nodeFor(message: any, docId: string): DocNode | undefined {
  if (message?.op === 'del') {
    return undefined;
  }
  const res = message?.res;
  const nodes: DocNode[] = Array.isArray(res) ? res : res ? [res] : [];
  return nodes.find(node => node?._id === docId);
}

export interface ScanGateParams {
  docId: string;
  /** Entity ref the doc hangs off, e.g. `prs=<id>` or `itm=<id>`. */
  refId: string;
  attr: DocumentType;
  /** Scan status already known for the doc; lets servable docs resolve sync. */
  currentVsts?: VirusScanStatus | null;
  timeoutMs?: number;
}

/**
 * A freshly uploaded doc isn't servable until its async virus scan finishes
 * (the backend answers 425/503 until then). This gate opens a short-lived
 * `docref` subscription scoped to the entity and emits once the scan settles —
 * the single source of truth for both the personal avatar and deal image.
 */
@Injectable({ providedIn: 'root' })
export class DocumentScanGate {
  private documentsService = inject(DocumentsService);
  private seq = 0;

  whenSettled(params: ScanGateParams): Observable<ScanOutcome> {
    // Short-circuit only when the caller actually knows the status. Omitting
    // currentVsts (undefined) means "verify over WS" — it must NOT be read as
    // servable, or a fresh upload commits before its scan finishes.
    if (params.currentVsts !== undefined && isServable(params.currentVsts)) {
      return of('ready');
    }

    // Unique mysid per call so each retry issues a fresh query.
    const settled$ = this.documentsService
      .subscribe(
        {
          mysid: `scan-gate-${params.docId}-${++this.seq}`,
          prms: { refId: params.refId, _attrs: `${params.attr}` },
        },
        true
      )
      .pipe(
        map(message => nodeFor(message, params.docId)),
        // Act on a terminal scan status; ignore still-pending updates.
        map((node): ScanOutcome | undefined => {
          if (!node) {
            return undefined;
          }
          if (isServable(node.vsts)) {
            return 'ready';
          }
          return isScanFailure(node.vsts) ? 'failed' : undefined;
        }),
        filter((outcome): outcome is ScanOutcome => !!outcome),
        take(1)
      );

    // Timer covers a scan that never reports back, so the caller can't hang.
    return race(
      settled$,
      timer(params.timeoutMs ?? DEFAULT_TIMEOUT_MS).pipe(
        map((): ScanOutcome => 'timeout')
      )
    );
  }
}
